import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import withStyles from '@material-ui/core/styles/withStyles';
import React, { useState, useMemo } from 'react';
import { resolveRedirectTo } from 'react-admin';

import DialogTitleWithCloseButton from '../../core/components/DialogTitleWithCloseButton';
import Confirm from '../../core/components/forked/layout/Confirm';
import RecipientsStep from './createsteps/RecipientsStep.js';
import OptionsStep from './createsteps/OptionsStep.js';
import SummaryStep from './createsteps/SummaryStep.js';
import { DistributionListContext } from './createsteps/DistributionListContext';

const styles = () => ({
  stepper: {
    backgroundColor: '#FFFFFF',
    padding: '0 0 0px',
    display: 'inline-flex'
  },
  leftWrapper: {
    flexGrow: 1
  },
  button: {
    '& span': {
      padding: 0
    }
  },
  backButton: {
    border: '1px solid #E0E0E0',
    '& span': {
      padding: 0
    }
  },
  saveButton: {
    border: '1px solid #E0E0E0',
    '& span': {
      padding: 0
    }
  },
  root: {
    paddingTop: '10px',
    paddingBottom: '10px'
  },
  paperWidthFalse: {
    height: 'inherit',
    width: '100%'
  },
  dialogContent: {
    display: 'flex',
    flex: 1,
    padding: '0px'
  },
  stepperContainer: {
    backgroundColor: '#FFFFFF',
    padding: '11px 8px',
    height: '24px',
    borderBottom: '1px solid #E0E0E0'
  },
  stepContainer: {
    display: 'flex',
    width: '100%',
    padding: '16px',
    overflowX: 'auto',
    '& .simple-form': {
      width: '100%'
    }
  }
});

const DistributionListCreate = props => {
  const [activeStep, setActiveStep] = useState(0);
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false);
  const [dlContext, setDlContext] = useState({});

  const providerDlContext = useMemo(() => ({ dlContext, setDlContext }), [
    dlContext,
    setDlContext
  ]);

  const { classes, permissions, ...rest } = props;

  // List of steps
  const steps = [
    {
      name: 'recipients',
      label: 'Create List of Recipients',
      component: RecipientsStep,
      nextButton: true,
      backButton: false
    },
    {
      name: 'options',
      label: 'Options',
      component: OptionsStep,
      nextButton: true,
      backButton: true
    },
    {
      name: 'summary',
      label: 'Summary',
      component: SummaryStep,
      nextButton: false,
      backButton: true
    }
  ];

  const hasNext = steps[activeStep] ? steps[activeStep].nextButton : false;
  const hasBack = steps[activeStep] ? steps[activeStep].backButton : false;

  const [isNextAvailable, setIsNextAvailable] = useState(true);
  const [preselectedUsersEmails, setPreselectedUsersEmails] = useState([]);
  const [userIdList, setUserIdList] = useState([]);

  const getActiveStep = () => {
    const step = steps[activeStep];
    return (
      <step.component
        {...rest}
        onSubmitEnd={onSubmitEnd}
        variant="standard"
        setIsNextAvailable={setIsNextAvailable}
        setPreselectedUsersEmails={setPreselectedUsersEmails}
        preselectedUsersEmails={preselectedUsersEmails}
        setUserIdList={setUserIdList}
        userIdList={userIdList}
        permissions={permissions}
      />
    );
  };

  const onSubmitEnd = async () => {
    if (activeStep === 2) {
      handleClose();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleCloseClick = () => {
    setConfirmDialogIsOpen(true);
  };

  const handleClose = () => {
    props.history.push(resolveRedirectTo('list', props.basePath));
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogIsOpen(false);
  };

  const handleNextForm = () => {
    // Trigger a submit to active form
    // This is needed because our modal action buttons are oustide the form
    dlContext.currentForm && dlContext.currentForm.submit();
  };

  const handleBackForm = () => {
    if (activeStep > 0) {
      setDlContext({ ...dlContext, currentForm: undefined });
      setActiveStep(activeStep - 1);
    }
    if (activeStep === 1) {
      setUserIdList([]);
      setPreselectedUsersEmails([]);
    }
  };

  return (
    <Dialog
      open={true}
      aria-label="Distribution List Create"
      maxWidth={false}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        if (reason === 'escapeKeyDown') return handleCloseClick();
      }}
      classes={{
        paperWidthFalse: classes.paperWidthFalse // class name, e.g. `classes-nesting-root-x`
      }}
    >
      <DialogTitleWithCloseButton onClose={handleCloseClick}>
        New Distribution List
      </DialogTitleWithCloseButton>
      <div className={classes.stepperContainer}>
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {steps.map(step => (
            <Step key={step.name}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </div>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.stepContainer}>
          <DistributionListContext.Provider value={providerDlContext}>
            {getActiveStep()}
          </DistributionListContext.Provider>
        </div>
        <Confirm
          isOpen={confirmDialogIsOpen}
          title="Close Window?"
          content={
            <div>
              <p style={{ margin: 0 }}>
                You are about to quit the create operation, and return to
                Distribution Lists page.
              </p>
              <br />
              Your progress on this step will not be saved.
            </div>
          }
          onConfirm={handleClose}
          onClose={handleConfirmDialogClose}
          confirm="ra.action.close"
        />
      </DialogContent>
      <DialogActions style={{ borderTop: `1px solid rgba(0, 0, 0, 0.12)` }}>
        <div className={classes.leftWrapper}>
          {hasBack && (
            <Button
              onClick={handleBackForm}
              variant="outlined"
              color="primary"
              className={classes.backButton}
            >
              Back
            </Button>
          )}
        </div>
        <Button
          onClick={handleClose}
          color="primary"
          variant="outlined"
          className={classes.button}
        >
          Close
        </Button>
        <Button
          onClick={handleNextForm}
          color="primary"
          variant="contained"
          className={classes.button}
          disabled={!isNextAvailable}
        >
          {hasNext ? 'Next' : 'Save Distribution List'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles)(DistributionListCreate);
